import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Compass,
  Package,
  BarChart3,
  Zap,
  MessageSquare,
  Bot,
  Settings as SettingsIcon,
  Search,
  Sparkles,
} from 'lucide-react';
import { FosSearchInput } from '@/components/ui/flipperos';
import { TOURS, useTour } from '@/components/onboarding/flipperos';

interface Command {
  id: string;
  label: string;
  hint?: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement> & { size?: number | string }>;
  /** Either a route to navigate to, or an arbitrary action. */
  to?: string;
  action?: () => void;
}

export interface FosCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FosCommandPalette({ open, onOpenChange }: FosCommandPaletteProps) {
  const navigate = useNavigate();
  const { startTour } = useTour();
  const [q, setQ] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Build the command list. Route ids match Sidebar entries so the palette is
  // a strict superset (jump-to-page). Tour entries appear at the bottom and
  // restart their tour from step 1 even if the user already completed it.
  const commands = useMemo<Command[]>(
    () => [
      {
        id: 'dashboard',
        label: 'Dashboard',
        hint: 'Go to overview',
        icon: LayoutDashboard,
        to: '/',
      },
      { id: 'sourcing', label: 'Sourcing', hint: 'Find new deals', icon: Compass, to: '/sourcing' },
      {
        id: 'inventory',
        label: 'Inventory',
        hint: 'Manage items',
        icon: Package,
        to: '/inventory',
      },
      {
        id: 'analytics',
        label: 'Analytics',
        hint: 'Revenue + cohorts',
        icon: BarChart3,
        to: '/analytics',
      },
      {
        id: 'automation',
        label: 'Automation',
        hint: 'Rules + agent runs',
        icon: Zap,
        to: '/automation',
      },
      {
        id: 'messages',
        label: 'Messages',
        hint: 'Customer threads',
        icon: MessageSquare,
        to: '/messages',
      },
      {
        id: 'agents',
        label: 'Agents',
        hint: 'Agent activity + settings',
        icon: Bot,
        to: '/agents/activity',
      },
      {
        id: 'settings',
        label: 'Settings',
        hint: 'Account + integrations',
        icon: SettingsIcon,
        to: '/settings',
      },
      ...TOURS.map<Command>((t) => ({
        id: `tour-${t.id}`,
        label: `Take a tour: ${t.name}`,
        hint: t.description,
        icon: Sparkles,
        action: () => startTour(t.id, { restart: true }),
      })),
    ],
    [startTour],
  );

  const filtered = useMemo(() => {
    if (!q.trim()) return commands;
    const needle = q.toLowerCase();
    return commands.filter(
      (c) => c.label.toLowerCase().includes(needle) || c.hint?.toLowerCase().includes(needle),
    );
  }, [commands, q]);

  // Reset state on open transitions
  useEffect(() => {
    if (open) {
      setQ('');
      setActiveIndex(0);
      // Defer focus until after the modal renders.
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [open]);

  // Clamp the active index when the result list shrinks.
  useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(Math.max(0, filtered.length - 1));
  }, [filtered.length, activeIndex]);

  if (!open) return null;

  const runCommand = (cmd: Command) => {
    onOpenChange(false);
    if (cmd.action) cmd.action();
    if (cmd.to) navigate(cmd.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onOpenChange(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filtered[activeIndex];
      if (cmd) runCommand(cmd);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[15vh]"
      onClick={() => onOpenChange(false)}
      onKeyDown={onKeyDown}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" aria-hidden />

      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'relative w-full max-w-[560px]',
          'bg-fos-bg-card border border-fos-border rounded-fos-md shadow-fos-lg',
          'animate-fos-fade-in overflow-hidden',
        )}
      >
        <div className="border-b border-fos-border p-3">
          <FosSearchInput
            ref={inputRef}
            value={q}
            onValueChange={setQ}
            placeholder="Type a command or search..."
            leadingIcon={<Search size={14} />}
          />
        </div>

        <ul
          className="fos-scrollbar max-h-[50vh] overflow-y-auto py-1"
          role="listbox"
          aria-label="Commands"
        >
          {filtered.length === 0 && (
            <li className="px-4 py-6 text-center text-[13px] text-fos-text-muted">
              No matches for &quot;{q}&quot;
            </li>
          )}
          {filtered.map((cmd, idx) => {
            const Icon = cmd.icon;
            const active = idx === activeIndex;
            return (
              <li
                key={cmd.id}
                role="option"
                aria-selected={active}
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => runCommand(cmd)}
                className={cn(
                  'flex items-center gap-3 px-4 py-2 cursor-pointer',
                  'transition-colors duration-fos text-[13px]',
                  active ? 'bg-fos-bg-hover text-fos-text-primary' : 'text-fos-text-secondary',
                )}
              >
                <Icon size={16} className={active ? 'text-fos-accent' : 'text-fos-text-muted'} />
                <span className="flex-1 truncate">{cmd.label}</span>
                {cmd.hint && (
                  <span className="max-w-[40%] truncate text-[11px] text-fos-text-muted">
                    {cmd.hint}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="flex items-center gap-3 border-t border-fos-border bg-fos-bg-surface px-4 py-2 font-mono text-[10px] text-fos-text-muted">
          <span>↑↓ navigate</span>
          <span>↵ select</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
